/**
 * Stor peranti: katalog, jualan, outbox.
 *
 * Peraturan yang dilaksanakan di sini (ADR-0002):
 *  - Jualan ditulis dalam SATU transaksi IndexedDB bersama baris, bayaran dan entri outbox.
 *    Sama ada semuanya wujud, atau tiada apa-apa.
 *  - `client_seq` menaik per peranti, diambil di dalam transaksi yang sama — jadi susunan
 *    terpelihara tanpa bergantung pada jam, dan dua tab tidak boleh mendapat nombor yang sama.
 *  - Nombor resit dijana secara tempatan; tiada perjalanan ke pelayan.
 *
 * Pengiraan wang menggunakan `@72on/domain` — fungsi yang SAMA dengan pelayan, supaya
 * jumlah tempatan dan jumlah yang disahkan pelayan sepadan.
 */

import { buildReceiptNo, computeSaleTotals, type LineInput } from '@72on/domain';
import { closeShift as closeShiftPure, newShift, type ClosedShift, type ShiftRecord } from './shift.js';
import { STORES, done, openDatabase, request } from './idb.js';
import type {
  DeviceIdentity,
  OutboxEntry,
  PaymentMethod,
  PaymentRecord,
  ProductRecord,
  SaleLineRecord,
  SaleRecord,
  StoredKeys,
  TaxRateRecord,
  TenantSettings,
  Unit,
  VariantRecord,
} from './types.js';

export interface CatalogSnapshot {
  seq: number;
  products: ProductRecord[];
  variants: VariantRecord[];
  tax_rates: TaxRateRecord[];
  settings: TenantSettings;
  /**
   * Nama kedai daripada pelayan. Peranti yang dipasangkan sebelum medan ini wujud menerimanya pada
   * segarkan katalog pertama — tanpa perlu memasangkan semula.
   */
  tenant_name?: string | null;
}

export type VariantWithProduct = VariantRecord & { product_name: string };

export interface SaleLineInput {
  variantId: string | null;
  description: string;
  qtyMilli: number;
  unit: Unit;
  unitPriceSen: number;
  taxRateBp: number;
  discountSen?: number;
  qtySource?: 'scan' | 'grams' | 'amount' | 'manual' | 'scale';
  unitCostSen?: number;
}

export interface CompleteSaleInput {
  lines: readonly SaleLineInput[];
  payments: ReadonlyArray<{ method: PaymentMethod; amountSen: number; refNo?: string | null }>;
  orderDiscountSen?: number;
  note?: string | null;
  cashierUserId?: string | null;
  /** Nama kasir untuk resit salinan kemudian. */
  cashierName?: string | null;
  /** Syif terbuka pada peranti ini. Disimpan pada jualan DAN dihantar ke pelayan. */
  shiftId?: string;
  now?: Date;
}

export interface CompletedSale {
  sale: SaleRecord;
  lines: SaleLineRecord[];
  payments: PaymentRecord[];
  outbox: OutboxEntry;
}

const META = {
  identity: 'identity',
  keys: 'keys',
  settings: 'settings',
  catalogSeq: 'catalog_seq',
  nextClientSeq: 'next_client_seq',
  agentToken: 'agent_token',
  /**
   * Token sesi KASIR (log masuk PIN).
   *
   * POS tidak menyimpan sebarang token pemilik: katalog dibaca dengan tandatangan peranti, dan
   * back office meminta kata laluan setiap kali dibuka. Sesi kasir di sini hanya untuk atribusi
   * jualan dan semakan sesi.
   */
  cashierToken: 'cashier_token',
} as const;

export class PosStore {
  private constructor(private readonly db: IDBDatabase) {}

  static async open(name?: string, factory?: IDBFactory): Promise<PosStore> {
    const db = await openDatabase(name, factory);
    return new PosStore(db);
  }

  close(): void {
    this.db.close();
  }

  // ---------------------------------------------------------------- meta

  private async getMeta<T>(key: string): Promise<T | undefined> {
    const tx = this.db.transaction(STORES.meta, 'readonly');
    const value = await request<T | undefined>(tx.objectStore(STORES.meta).get(key));
    await done(tx);
    return value;
  }

  private async setMeta(key: string, value: unknown): Promise<void> {
    const tx = this.db.transaction(STORES.meta, 'readwrite');
    tx.objectStore(STORES.meta).put(value, key);
    await done(tx);
  }

  getIdentity(): Promise<DeviceIdentity | undefined> {
    return this.getMeta<DeviceIdentity>(META.identity);
  }

  saveIdentity(identity: DeviceIdentity): Promise<void> {
    return this.setMeta(META.identity, identity);
  }

  getKeys(): Promise<StoredKeys | undefined> {
    return this.getMeta<StoredKeys>(META.keys);
  }

  saveKeys(keys: StoredKeys): Promise<void> {
    return this.setMeta(META.keys, keys);
  }

  async getSettings(): Promise<TenantSettings> {
    return (await this.getMeta<TenantSettings>(META.settings)) ?? {};
  }

  async getCatalogSeq(): Promise<number> {
    return (await this.getMeta<number>(META.catalogSeq)) ?? 0;
  }



  /** Token sesi kasir (PIN), berasingan daripada token back office. */
  getCashierToken(): Promise<string | undefined> {
    return this.getMeta<string>(META.cashierToken);
  }

  saveCashierToken(token: string): Promise<void> {
    return this.setMeta(META.cashierToken, token);
  }

  clearCashierToken(): Promise<void> {
    return this.setMeta(META.cashierToken, undefined);
  }

  // ------------------------------------------------------------- katalog

  /** Ganti katalog dengan snapshot. Satu transaksi: peranti tidak pernah melihat separuh katalog. */
  async applySnapshot(snapshot: CatalogSnapshot): Promise<void> {
    const tx = this.db.transaction(
      [STORES.products, STORES.variants, STORES.taxRates, STORES.meta],
      'readwrite',
    );
    const products = tx.objectStore(STORES.products);
    const variants = tx.objectStore(STORES.variants);
    const taxRates = tx.objectStore(STORES.taxRates);

    products.clear();
    variants.clear();
    taxRates.clear();

    for (const product of snapshot.products) products.put(product);
    for (const variant of snapshot.variants) variants.put(variant);
    for (const rate of snapshot.tax_rates) taxRates.put(rate);

    tx.objectStore(STORES.meta).put(snapshot.settings ?? {}, META.settings);
    tx.objectStore(STORES.meta).put(snapshot.seq, META.catalogSeq);

    await done(tx);

    /**
     * Simpan nama kedai dalam identiti peranti.
     *
     * Ditulis di sini, bukan hanya semasa pairing, supaya peranti yang dipasangkan sebelum medan ini
     * wujud mendapat namanya pada segarkan katalog pertama. Identiti sedia ada digabung, bukan
     * ditulis semula — kunci dan nombor peranti tidak berubah.
     */
    if (typeof snapshot.tenant_name === 'string' && snapshot.tenant_name !== '') {
      const identity = await this.getIdentity();
      if (identity !== undefined && identity.tenantName !== snapshot.tenant_name) {
        await this.saveIdentity({ ...identity, tenantName: snapshot.tenant_name });
      }
    }
  }

  async listVariants(): Promise<VariantWithProduct[]> {
    const tx = this.db.transaction([STORES.variants, STORES.products], 'readonly');
    const variants = await request<VariantRecord[]>(tx.objectStore(STORES.variants).getAll());
    const products = await request<ProductRecord[]>(tx.objectStore(STORES.products).getAll());
    await done(tx);

    const names = new Map(products.map((product) => [product.id, product.name]));
    return variants
      .filter((variant) => variant.is_active)
      .map((variant) => ({
        ...variant,
        product_name: names.get(variant.product_id) ?? productNameFallback(variant),
      }));
  }

  async searchVariants(query: string, limit = 40): Promise<VariantWithProduct[]> {
    const all = await this.listVariants();
    const needle = query.trim().toLowerCase();
    if (needle === '') return all.slice(0, limit);

    const matches = all.filter((variant) => {
      const haystack = `${variant.product_name} ${variant.name ?? ''} ${variant.sku ?? ''} ${variant.barcode ?? ''}`.toLowerCase();
      return haystack.includes(needle);
    });
    return matches.slice(0, limit);
  }

  async findByBarcode(barcode: string): Promise<VariantWithProduct | undefined> {
    const tx = this.db.transaction([STORES.variants, STORES.products], 'readonly');
    const found = await request<VariantRecord | undefined>(
      tx.objectStore(STORES.variants).index('barcode').get(barcode),
    );
    const product =
      found === undefined
        ? undefined
        : await request<ProductRecord | undefined>(
            tx.objectStore(STORES.products).get(found.product_id),
          );
    await done(tx);

    if (found === undefined) return undefined;
    return { ...found, product_name: product?.name ?? productNameFallback(found) };
  }

  // -------------------------------------------------------------- jualan

  /**
   * Selesaikan jualan dan tulis ke outbox, semuanya atau tiada apa-apa.
   *
   * Ia TIDAK memerlukan rangkaian. Itu tujuan keseluruhan fail ini.
   */
  async completeSale(input: CompleteSaleInput): Promise<CompletedSale> {
    const identity = await this.getIdentity();
    if (identity === undefined) throw new Error('Peranti belum dipasangkan');
    if (input.lines.length === 0) throw new Error('Jualan mesti mempunyai sekurang-kurangnya satu baris');
    if (input.payments.length === 0) throw new Error('Jualan mesti mempunyai sekurang-kurangnya satu bayaran');

    const settings = await this.getSettings();
    const taxInclusive = settings.tax_inclusive === true;
    /**
     * Cukai dimatikan (lalai) → kadar 0 untuk semua baris.
     *
     * Pelayan melakukan perkara yang sama, jadi walaupun peranti ini tersalah, cukai tidak akan
     * dikenakan pada jualan yang sampai ke pelayan.
     */
    const taxEnabled = settings.tax_enabled === true;

    const lineInputs: LineInput[] = input.lines.map((line) => ({
      qtyMilli: line.qtyMilli,
      unitPriceSen: line.unitPriceSen,
      taxRateBp: taxEnabled ? line.taxRateBp : 0,
      taxInclusive,
      ...(line.discountSen === undefined ? {} : { discountSen: line.discountSen }),
    }));

    const totals = computeSaleTotals({
      lines: lineInputs,
      ...(input.orderDiscountSen === undefined ? {} : { orderDiscountSen: input.orderDiscountSen }),
      cashRounding: settings.rounding ?? 'none',
    });

    const paid = input.payments.reduce((sum, payment) => sum + payment.amountSen, 0);
    if (paid < totals.totalSen) {
      throw new Error(
        `Bayaran (${paid} sen) kurang daripada jumlah (${totals.totalSen} sen)`,
      );
    }

    const now = input.now ?? new Date();
    const saleId = crypto.randomUUID();
    const createdAt = now.toISOString();

    const tx = this.db.transaction(
      [STORES.meta, STORES.sales, STORES.saleLines, STORES.payments, STORES.outbox],
      'readwrite',
    );

    const metaStore = tx.objectStore(STORES.meta);
    const previousSeq = await request<number | undefined>(metaStore.get(META.nextClientSeq));
    const clientSeq = (previousSeq ?? 0) + 1;
    metaStore.put(clientSeq, META.nextClientSeq);

    const receiptNo = buildReceiptNo({
      outletCode: identity.outletCode,
      deviceNo: identity.deviceNo,
      date: now,
      seq: clientSeq,
    });

    const sale: SaleRecord = {
      id: saleId,
      client_seq: clientSeq,
      receipt_no: receiptNo,
      outlet_id: identity.outletId,
      device_id: identity.deviceId,
      cashier_user_id: input.cashierUserId ?? null,
      cashier_name: input.cashierName ?? null,
      client_created_at: createdAt,
      subtotal_sen: totals.subtotalSen,
      discount_sen: totals.discountSen,
      tax_sen: totals.taxSen,
      rounding_sen: totals.roundingSen,
      total_sen: totals.totalSen,
      paid_sen: paid,
      change_sen: paid - totals.totalSen,
      synced_at: null,
      note: input.note ?? null,
    };
    tx.objectStore(STORES.sales).put(sale);

    const lineRecords: SaleLineRecord[] = [];
    for (const [index, line] of input.lines.entries()) {
      const computed = totals.lines[index];
      if (computed === undefined) throw new Error('baris domain tidak sepadan');
      const record: SaleLineRecord = {
        id: crypto.randomUUID(),
        sale_id: saleId,
        line_no: index + 1,
        variant_id: line.variantId,
        description_snapshot: line.description,
        qty: (line.qtyMilli / 1000).toFixed(3),
        unit: line.unit,
        unit_price_sen: line.unitPriceSen,
        unit_cost_sen: line.unitCostSen ?? 0,
        discount_sen: computed.discountSen,
        tax_rate_bp: line.taxRateBp,
        tax_sen: computed.taxSen,
        line_total_sen: computed.lineTotalSen,
        qty_source: line.qtySource ?? 'manual',
      };
      lineRecords.push(record);
      tx.objectStore(STORES.saleLines).put(record);
    }

    const paymentRecords: PaymentRecord[] = [];
    for (const payment of input.payments) {
      const record: PaymentRecord = {
        id: crypto.randomUUID(),
        sale_id: saleId,
        method: payment.method,
        amount_sen: payment.amountSen,
        ref_no: payment.refNo ?? null,
      };
      paymentRecords.push(record);
      tx.objectStore(STORES.payments).put(record);
    }

    const outbox: OutboxEntry = {
      client_seq: clientSeq,
      type: 'sale.completed',
      payload: {
        id: saleId,
        receipt_no: receiptNo,
        client_created_at: createdAt,
        // Syif mesti ikut jualan: pelayan mengira Z-report daripada jualan yang bertanda syif ini.
        ...(input.shiftId === undefined ? {} : { shift_id: input.shiftId }),
        lines: lineRecords.map((line) => ({
          line_no: line.line_no,
          variant_id: line.variant_id,
          description_snapshot: line.description_snapshot,
          qty: line.qty,
          unit: line.unit,
          unit_price_sen: line.unit_price_sen,
          discount_sen: line.discount_sen,
          tax_rate_bp: line.tax_rate_bp,
          qty_source: line.qty_source,
        })),
        payments: paymentRecords.map((payment) => ({
          method: payment.method,
          amount_sen: payment.amount_sen,
          ...(payment.ref_no === null ? {} : { ref_no: payment.ref_no }),
        })),
        totals: {
          subtotal_sen: totals.subtotalSen,
          discount_sen: totals.discountSen,
          tax_sen: totals.taxSen,
          total_sen: totals.totalSen,
          paid_sen: paid,
          change_sen: paid - totals.totalSen,
        },
        ...(input.note === undefined || input.note === null ? {} : { note: input.note }),
      },
      status: 'pending',
      attempts: 0,
      last_error: null,
      created_at: createdAt,
    };
    tx.objectStore(STORES.outbox).put(outbox);

    await done(tx);

    return { sale, lines: lineRecords, payments: paymentRecords, outbox };
  }

  // -------------------------------------------------------------- outbox

  async pendingOutbox(limit = 100): Promise<OutboxEntry[]> {
    const tx = this.db.transaction(STORES.outbox, 'readonly');
    const all = await request<OutboxEntry[]>(
      tx.objectStore(STORES.outbox).index('status').getAll('pending'),
    );
    await done(tx);
    return all.sort((a, b) => a.client_seq - b.client_seq).slice(0, limit);
  }

  async pendingCount(): Promise<number> {
    return (await this.pendingOutbox(1000)).length;
  }

  async markSynced(clientSeqs: readonly number[]): Promise<void> {
    if (clientSeqs.length === 0) return;
    const tx = this.db.transaction([STORES.outbox, STORES.sales], 'readwrite');
    const outbox = tx.objectStore(STORES.outbox);
    const sales = tx.objectStore(STORES.sales);
    const salesBySeq = await request<SaleRecord[]>(sales.index('client_seq').getAll());

    for (const seq of clientSeqs) {
      const entry = await request<OutboxEntry | undefined>(outbox.get(seq));
      if (entry !== undefined) {
        outbox.put({ ...entry, status: 'synced', last_error: null });
      }
      const sale = salesBySeq.find((candidate) => candidate.client_seq === seq);
      if (sale !== undefined) {
        sales.put({ ...sale, synced_at: new Date().toISOString() });
      }
    }
    await done(tx);
  }

  async markRejected(clientSeqs: readonly number[], message: string): Promise<void> {
    if (clientSeqs.length === 0) return;
    const tx = this.db.transaction(STORES.outbox, 'readwrite');
    const outbox = tx.objectStore(STORES.outbox);
    for (const seq of clientSeqs) {
      const entry = await request<OutboxEntry | undefined>(outbox.get(seq));
      if (entry !== undefined) {
        // Rekod TIDAK dipadam: kasir sudah ambil wang, dan rekod itu satu-satunya bukti.
        outbox.put({
          ...entry,
          status: 'rejected',
          attempts: entry.attempts + 1,
          last_error: message,
        });
      }
    }
    await done(tx);
  }

  async recordAttempt(clientSeqs: readonly number[], message: string): Promise<void> {
    if (clientSeqs.length === 0) return;
    const tx = this.db.transaction(STORES.outbox, 'readwrite');
    const outbox = tx.objectStore(STORES.outbox);
    for (const seq of clientSeqs) {
      const entry = await request<OutboxEntry | undefined>(outbox.get(seq));
      if (entry !== undefined) {
        outbox.put({ ...entry, attempts: entry.attempts + 1, last_error: message });
      }
    }
    await done(tx);
  }

  async recentSales(limit = 10): Promise<SaleRecord[]> {
    const tx = this.db.transaction(STORES.sales, 'readonly');
    const all = await request<SaleRecord[]>(tx.objectStore(STORES.sales).getAll());
    await done(tx);
    return all.sort((a, b) => b.client_seq - a.client_seq).slice(0, limit);
  }

  /**
   * Jualan terkini LENGKAP (jualan + baris + bayaran) untuk cetak semula.
   *
   * Jualan yang sudah dihantar ke pelayan kekal dalam IndexedDB — itulah satu-satunya salinan
   * tempatan apabila pelanggan meminta resit kemudian dan rangkaian mati.
   */
  async salesForReprint(
    limit = 20,
  ): Promise<Array<{ sale: SaleRecord; lines: SaleLineRecord[]; payments: PaymentRecord[] }>> {
    const sales = await this.recentSales(limit);
    if (sales.length === 0) return [];

    const tx = this.db.transaction([STORES.saleLines, STORES.payments], 'readonly');
    const lineIndex = tx.objectStore(STORES.saleLines).index('sale_id');
    const paymentIndex = tx.objectStore(STORES.payments).index('sale_id');

    const result: Array<{ sale: SaleRecord; lines: SaleLineRecord[]; payments: PaymentRecord[] }> = [];
    for (const sale of sales) {
      const lines = await request<SaleLineRecord[]>(lineIndex.getAll(sale.id));
      const payments = await request<PaymentRecord[]>(paymentIndex.getAll(sale.id));
      result.push({ sale, lines, payments });
    }
    await done(tx);
    return result;
  }

  // ---------------------------------------------------------------- syif

  /** Syif terbuka bagi peranti ini, jika ada. */
  async getOpenShift(): Promise<ShiftRecord | undefined> {
    const tx = this.db.transaction(STORES.shifts, 'readonly');
    const all = await request<ShiftRecord[]>(tx.objectStore(STORES.shifts).getAll());
    await done(tx);
    return all.find((shift) => shift.status === 'open');
  }

  async openShift(
    openingCashSen: number,
    cashier?: { userId: string; name: string },
    now: Date = new Date(),
  ): Promise<ShiftRecord> {
    if ((await this.getOpenShift()) !== undefined) {
      throw new Error('Sudah ada syif terbuka pada peranti ini');
    }
    const shift: ShiftRecord = {
      ...newShift(openingCashSen, now),
      cashier_user_id: cashier?.userId ?? null,
      cashier_name: cashier?.name ?? null,
    };
    const tx = this.db.transaction(STORES.shifts, 'readwrite');
    tx.objectStore(STORES.shifts).put(shift);
    await done(tx);
    return shift;
  }

  /** Jualan (dengan bayaran) sejak satu cap masa. Digunakan oleh tutup syif. */
  async salesWithPaymentsSince(sinceIso: string): Promise<Array<{ sale: SaleRecord; payments: PaymentRecord[] }>> {
    const tx = this.db.transaction([STORES.sales, STORES.payments], 'readonly');
    const sales = await request<SaleRecord[]>(tx.objectStore(STORES.sales).getAll());
    const paymentIndex = tx.objectStore(STORES.payments).index('sale_id');

    const result: Array<{ sale: SaleRecord; payments: PaymentRecord[] }> = [];
    for (const sale of sales) {
      if (sale.client_created_at < sinceIso) continue;
      const payments = await request<PaymentRecord[]>(paymentIndex.getAll(sale.id));
      result.push({ sale, payments });
    }
    await done(tx);
    return result;
  }

  /** Tutup syif: kira tunai sepatutnya daripada jualan, bandingkan dengan kiraan fizikal. */
  async closeShift(countedCashSen: number, now: Date = new Date()): Promise<ClosedShift> {
    const shift = await this.getOpenShift();
    if (shift === undefined) throw new Error('Tiada syif terbuka untuk ditutup');

    const entries = await this.salesWithPaymentsSince(shift.opened_at);
    const closed = closeShiftPure({ shift, entries, countedCashSen, now });

    const tx = this.db.transaction(STORES.shifts, 'readwrite');
    tx.objectStore(STORES.shifts).put(closed.shift);
    await done(tx);
    return closed;
  }

  /** Syif yang belum diterima pelayan. Dihantar semula pada setiap sync. */
  async listUnuploadedShifts(limit = 20): Promise<ShiftRecord[]> {
    const tx = this.db.transaction(STORES.shifts, 'readonly');
    const all = await request<ShiftRecord[]>(tx.objectStore(STORES.shifts).getAll());
    await done(tx);
    return all
      .filter((shift) => shift.uploaded_at === undefined || shift.uploaded_at === null)
      .sort((a, b) => a.opened_at.localeCompare(b.opened_at))
      .slice(0, limit);
  }

  /** Tandakan syif sebagai diterima pelayan. */
  async markShiftUploaded(id: string, at: string = new Date().toISOString()): Promise<void> {
    const tx = this.db.transaction(STORES.shifts, 'readwrite');
    const store = tx.objectStore(STORES.shifts);
    const shift = await request<ShiftRecord | undefined>(store.get(id));
    if (shift !== undefined) store.put({ ...shift, uploaded_at: at });
    await done(tx);
  }

  async listShifts(limit = 20): Promise<ShiftRecord[]> {
    const tx = this.db.transaction(STORES.shifts, 'readonly');
    const all = await request<ShiftRecord[]>(tx.objectStore(STORES.shifts).getAll());
    await done(tx);
    return all.sort((a, b) => b.opened_at.localeCompare(a.opened_at)).slice(0, limit);
  }

  // ---------------------------------------------------------------- ejen

  /**
   * Tetapkan permulaan `client_seq` selepas pairing.
   *
   * Peranti pengganti mesti meneruskan penomboran resit cawangan, bukan bermula di 0001 — jika tidak
   * ia akan mencetak nombor yang sudah digunakan PC terdahulu.
   */
  async seedClientSeq(value: number): Promise<void> {
    if (!Number.isFinite(value) || value < 0) return;
    const tx = this.db.transaction(STORES.meta, 'readwrite');
    tx.objectStore(STORES.meta).put(Math.floor(value), META.nextClientSeq);
    await done(tx);
  }
  getAgentToken(): Promise<string | undefined> {
    return this.getMeta<string>(META.agentToken);
  }

  saveAgentToken(token: string): Promise<void> {
    return this.setMeta(META.agentToken, token);
  }
}

function productNameFallback(variant: VariantRecord): string {
  return variant.name ?? 'Barang';
}
